import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { ensureAccessToken } from '../ensure-access-token';

/**
 * Admits a route only while `flagName` is on for this member, and sends them to `fallbackPath`
 * otherwise. Decides on the member's own value: app flags say only whether a row is switched on,
 * not who it targets, so admitting on them alone let anyone into a page gated on a flag targeted at
 * someone else. Values held for a member who has since signed out are not this member's.
 *
 * When the member's own value cannot be had (no session, or user-flags failed), a flag on for
 * everyone still admits on the app value, so a network blip does not lock members out of Courses.
 * A `targeted` flag does not: it reaches only the members it names, so it turns them away instead.
 * @param {string} flagName
 * @param {string} [fallbackPath]
 * @param {{ targeted?: boolean }} [options]
 * @returns {Promise<string|undefined>} the path to go to instead, or undefined to admit
 */
export async function requireFeatureFlag(
  flagName,
  fallbackPath = '/tabs/home',
  { targeted = false } = {},
) {
  const { isEnabled, hasMemberValue, loadAppFlags, loadUserFlags } = useFeatureFlags();

  if (!isEnabled(flagName)) {
    await loadAppFlags();
  }

  const token = await ensureAccessToken(`feature-flag-${flagName}`);
  if (!isEnabled(flagName) || !hasMemberValue(flagName, token)) {
    await loadUserFlags(token);
  }

  if (!isEnabled(flagName)) return fallbackPath;
  if (targeted && !hasMemberValue(flagName, token)) return fallbackPath;
}
